import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { activeCurrency, formatMoneyExact, formatDate } from "@/lib/format";
import { NumInput } from "@/components/shared/NumInput";
import { fxRateOf } from "@/lib/documents";
import { Segmented, InfoTip } from "@/components/settings/Grouped";

// 321: měna dokladu. Doklad v cizí měně se při uložení převede kurzem ČNB k datu vystavení na měnu
// firmy (jako v Pohodě) — náklady, platby i DPH jsou pak v jedné měně. Převedený doklad ukazuje
// částku z papíru a kurz; kurz jde opravit (přepočet z částek na papíře).
const OTHER = { CZK: "EUR", EUR: "CZK" };
const SYM = { CZK: "Kč", EUR: "€" };
const rateText = (r) => Number(r).toLocaleString("cs-CZ", { maximumFractionDigits: 4 });
const money = (v, cur) => (SYM[cur] ? formatMoneyExact(v, cur)
  : `${Number(v || 0).toLocaleString("cs-CZ", { minimumFractionDigits: 2, maximumFractionDigits: 2 })} ${cur}`);
const num = (v) => Number(String(v ?? "").replace(",", "."));

export function DocCurrency({ doc, setDoc, totalKey = "total_with_vat", dateKey = "issue_date", kind = "Faktura", className = "" }) {
  const base = activeCurrency();
  const fx = doc.foreign;
  const cur = (doc.currency || base).toUpperCase();
  const pending = !fx && cur !== base;                 // částky ve formuláři jsou v cizí měně, převod při uložení
  const day = (doc[dateKey] || "").slice(0, 10);
  const [cnb, setCnb] = useState(null);
  const [editRate, setEditRate] = useState(false);
  useEffect(() => {
    if (!pending) { setCnb(null); return undefined; }
    let alive = true;
    setCnb(null);
    api.get("/fx-rate", { params: { currency: cur, date: day || undefined } })
      .then((r) => alive && setCnb(r.data)).catch(() => alive && setCnb({ error: true }));
    return () => { alive = false; };
  }, [pending, cur, day]);
  const set = (patch) => setDoc((d) => ({ ...d, ...patch }));
  const total = num(doc[totalKey]) || 0;
  const manual = fxRateOf(doc);

  if (fx) {
    const paper = fx[totalKey] ?? fx.total_with_vat ?? fx.total;
    return (
      <div className={`rounded-[12px] bg-secondary/60 px-3 py-2.5 text-[13px] space-y-2 ${className}`} data-testid="doc-foreign">
        <div className="flex flex-wrap items-center gap-x-2 gap-y-1">
          <span className="font-semibold">{kind} v {fx.currency}</span>
          <span className="tabular-nums whitespace-nowrap">{money(paper, fx.currency)}</span>
          <span className="text-muted-foreground">· kurz {rateText(fx.rate)} {fx.source === "ČNB" ? `ČNB ${formatDate(fx.date)}` : "ručně"}</span>
          {!editRate && (
            <button type="button" onClick={() => setEditRate(true)} data-testid="doc-fx-edit"
              className="ml-auto min-h-[32px] font-semibold text-brand hover:underline">Změnit kurz</button>
          )}
        </div>
        {editRate && (
          <div className="flex flex-wrap items-center gap-2">
            <NumInput value={doc.fx_rate ?? fx.rate} onChange={(v) => set({ fx_rate: v })} aria-label="Kurz"
              className="h-10 w-28 text-right tabular-nums text-base md:text-sm" data-testid="doc-fx-rate" />
            <span className="text-muted-foreground">{SYM[base]} za 1 {fx.currency} — částky se přepočítají z {money(paper, fx.currency)}</span>
          </div>
        )}
      </div>
    );
  }

  const options = [{ value: base, label: SYM[base], testid: `doc-currency-${base}` },
    { value: OTHER[base], label: SYM[OTHER[base]], testid: `doc-currency-${OTHER[base]}` }];
  if (!options.some((o) => o.value === cur)) options.push({ value: cur, label: cur, testid: `doc-currency-${cur}` });
  const rate = manual ?? cnb?.rate;
  return (
    <div className={`space-y-2 ${className}`}>
      <div className="flex items-center gap-2">
        <span className="text-[13px] text-muted-foreground">Měna na dokladu</span>
        <InfoTip label="Doklad v cizí měně" text={`Částky opište tak, jak jsou na dokladu. Při uložení se převedou na ${SYM[base]} kurzem ČNB ke dni vystavení — v nákladech zakázky, platbách i DPH pak počítáte v jedné měně. Původní částka a kurz zůstanou u dokladu.`} />
        <span className="flex-1" />
        <Segmented options={options} value={cur} onChange={(v) => set({ currency: v, fx_rate: undefined })} testid="doc-currency" />
      </div>
      {pending && (
        <div className="rounded-[12px] bg-secondary/60 px-3 py-2 text-[13px] flex flex-wrap items-center gap-x-2 gap-y-1.5" data-testid="doc-fx-row">
          <span className="text-muted-foreground">Kurz</span>
          <NumInput value={doc.fx_rate ?? (cnb?.rate ?? "")} onChange={(v) => set({ fx_rate: v })} aria-label="Kurz"
            className="h-10 w-28 text-right tabular-nums text-base md:text-sm" data-testid="doc-fx-rate" />
          <span className="text-muted-foreground">
            {manual != null ? "zadáno ručně" : cnb?.rate ? `ČNB ${formatDate(cnb.date)}` : cnb?.error ? "ČNB nedostupná — zadejte kurz" : "načítám kurz ČNB…"}
          </span>
          {rate > 0 && total > 0 && (
            <span className="ml-auto font-semibold tabular-nums whitespace-nowrap" data-testid="doc-fx-base">= {formatMoneyExact(total * rate, base)}</span>
          )}
        </div>
      )}
    </div>
  );
}
